import { Connected, ServerOnline, UserLoggedOut } from "event-definitions"
import {
    enqueueAudit,
    getAllQueuedAudits,
    removeQueuedAudit,
    updateQueuedAuditMetadata,
    clearAllQueuedAudits,
} from "lib/@offline/local-audit-queue"
import { withTimeout } from "lib/@offline/with-timeout"
import { generate } from "library/guid/index.mjs"
import { invalidate } from "lib/graphql/cache"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { createAudit as createAuditNow } from "routes/audit/controller/create-audit"
import { updateAudit as updateAuditNow } from "routes/audit/controller/update-audit"
import { deleteJustification as deleteJustificationNow } from "routes/audit/controller/delete-justification"
import { createTailoredAuditReason as createTailoredAuditReasonNow } from "routes/regime/extension-edit/plugins/tailored-schedule-auditing/controller/create-tailored-audit-reason"
import { updateTailoredAuditReason as updateTailoredAuditReasonNow } from "routes/regime/extension-edit/plugins/tailored-schedule-auditing/controller/update-tailored-audit-reason"

const SYNC_TIMEOUT_MS = 30000
const ATTEMPT_TIMEOUT_MS = 30000
const SYNC_COOLDOWN_MS = 5 * 60 * 1000
const PERIODIC_SYNC_INTERVAL_MS = 30000
const CROSS_TAB_LOCK = "sfg20-audit-queue-sync"

const QUEUED_AUDIT = { queued: true }

const operations = {}

const queuedCreateAudit = queueable("createAudit", createAuditNow)
export const updateAudit = queueable("updateAudit", updateAuditNow, updateAuditDedupeKey)
export const deleteJustification = queueable("deleteJustification", deleteJustificationNow, auditIdDedupeKey)
export const createTailoredAuditReason = queueable(
    "createTailoredAuditReason",
    createTailoredAuditReasonNow,
    tailoredUpsertDedupeKey
)
export const updateTailoredAuditReason = queueable(
    "updateTailoredAuditReason",
    updateTailoredAuditReasonNow,
    auditIdDedupeKey
)

export function createAudit(shortDescription, longDescription, type, referenceId, payload, auditId = generate()) {
    return queuedCreateAudit(shortDescription, longDescription, type, referenceId, payload, auditId)
}

let syncing = false
let started = false

export function isNetworkError(error) {
    if (!navigator.onLine) return true
    if (!error) return false
    if (error.networkError) return true
    const message = error.message ?? ""
    return /failed to fetch|network ?error|load failed|operation timed out|server is unreachable/i.test(message)
}

export async function syncQueuedAudits() {
    if (syncing) return
    syncing = true
    try {
        if (navigator.locks) {
            await navigator.locks.request(CROSS_TAB_LOCK, { ifAvailable: true }, async (lock) => {
                if (lock) await replayQueue()
            })
        } else {
            await replayQueue()
        }
    } finally {
        syncing = false
    }
}

export function startAuditSyncManager() {
    if (started) return
    started = true

    Connected.on(syncQueuedAudits)
    ServerOnline.on(syncQueuedAudits)
    UserLoggedOut.on(clearAllQueuedAudits)
    window.addEventListener("online", syncQueuedAudits)
    setInterval(() => {
        if (navigator.onLine) syncQueuedAudits()
    }, PERIODIC_SYNC_INTERVAL_MS)
}

function queueable(name, operation, dedupeKey = noDedupe) {
    operations[name] = operation
    return queued

    function queued(...args) {
        return attemptOrQueue(operation, name, args, dedupeKey(...args))
    }
}

function noDedupe() {
    return undefined
}

function auditIdDedupeKey(auditId) {
    return auditId
}

async function attemptOrQueue(operation, operationName, args, dedupeKey) {
    if (!navigator.onLine) return queueOrWarn(operationName, args, dedupeKey)
    try {
        const result = await withTimeout(operation(...args), ATTEMPT_TIMEOUT_MS)
        if (result == null) return queueOrWarn(operationName, args, dedupeKey)
        return result
    } catch (e) {
        if (isNetworkError(e)) return queueOrWarn(operationName, args, dedupeKey)
        throw e
    }
}

async function queueOrWarn(operationName, args, dedupeKey) {
    const queued = await enqueueAudit(operationName, args, dedupeKey)
    if (queued) return QUEUED_AUDIT
    errorSnackbar("Unable to record this change in the audit trail. Your browser storage may be full or blocked.")
    return null
}

async function replayQueue() {
    let replayed = 0
    const held = new Set()
    try {
        const entries = await getAllQueuedAudits()
        for (const entry of entries) {
            const identity = entryIdentity(entry)
            if (held.has(identity)) continue
            if (entry.syncFailedAt && Date.now() - entry.syncFailedAt < SYNC_COOLDOWN_MS) {
                held.add(identity)
                continue
            }
            const operation = operations[entry.operation]
            if (!operation) {
                console.warn("Audit replay dropped entry with unknown operation", { key: entry.key })
                await removeQueuedAudit(entry.key)
                continue
            }
            try {
                const result = await withTimeout(operation(...entry.args), SYNC_TIMEOUT_MS)
                if (result == null) {
                    console.warn("Audit replay returned no result, will retry after cooldown", { key: entry.key })
                    await holdEntry(entry, held)
                    continue
                }
                await removeQueuedAudit(entry.key)
                replayed++
            } catch (e) {
                if (isNetworkError(e)) return
                console.warn("Audit replay rejected by server, will retry after cooldown", {
                    key: entry.key,
                    message: e?.message || String(e),
                })
                await holdEntry(entry, held)
            }
        }
    } finally {
        if (replayed) {
            invalidate("getAuditsByReferenceIdAndType")
            invalidate("getTailoredAudits")
        }
    }
}

async function holdEntry(entry, held) {
    await updateQueuedAuditMetadata(entry.key, { syncFailedAt: Date.now() })
    held.add(entryIdentity(entry))
}

function entryIdentity(entry) {
    return entry.key.slice(entry.key.indexOf(":") + 1)
}

function updateAuditDedupeKey({ auditId, type, referenceId, payload }) {
    if (auditId) return auditId
    const identity = [type, referenceId, payload?.changeType, payload?.taskId].filter(Boolean).join("|")
    return identity || undefined
}

function tailoredUpsertDedupeKey(audit) {
    const identity = [audit?.referenceId, audit?.scheduleId, audit?.scheduleVersion, audit?.taskId]
        .filter(Boolean)
        .join("|")
    return identity || undefined
}
